import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { createBrowserOcrEngine } from '../core/ocrWeb';
import { createPdfRenderer } from '../core/render';
import { runSplitPipeline, type PipelineOutput } from '../core/pipeline';
import type { JobRecord } from '../core/types';
import type { RunState } from '../App';

interface Props {
  run: RunState;
  onFinished: (job: JobRecord | null, pipeline: PipelineOutput) => void;
  onError: (message: string) => void;
  onCancel: () => void;
}

const STAGES = [
  { key: 'upload', label: '上传' },
  { key: 'parse', label: '解析' },
  { key: 'recognize', label: '识别' },
  { key: 'group', label: '分组' },
  { key: 'generate', label: '生成文件' },
  { key: 'done', label: '完成' },
] as const;

async function detectBackend(): Promise<'WebGPU' | 'WASM'> {
  try {
    const nav = navigator as unknown as {
      gpu?: { requestAdapter(): Promise<unknown> | null };
    };
    if (nav.gpu && (await nav.gpu.requestAdapter())) return 'WebGPU';
  } catch {
    /* ignore */
  }
  return 'WASM';
}

export default function ProcessingPage({ run, onFinished, onError, onCancel }: Props) {
  const isLocal = run.job === null;
  const [waiting, setWaiting] = useState(run.job?.status === 'queued');
  const [stage, setStage] = useState<string>('upload');
  const [stageDetail, setStageDetail] = useState('');
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [statusText, setStatusText] = useState('正在启动…');
  const startedAtRef = useRef<number>(Date.now());
  const cancelledRef = useRef(false);

  const elapsedTimer = useCallback(() => {
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const cleanup = elapsedTimer();
    return cleanup;
  }, [elapsedTimer]);

  useEffect(() => {
    let disposed = false;
    // React StrictMode (dev) runs effect -> cleanup -> effect; without this
    // reset the cleanup from the first run would cancel the second run too.
    cancelledRef.current = false;

    async function waitForProcessing(jobId: string): Promise<JobRecord> {
      // Poll until the server promotes this job to `processing`.
      for (;;) {
        const job = await api.getJob(jobId);
        if (disposed) throw new Error('已取消');
        if (job.status === 'processing') return job;
        if (job.status === 'failed') throw new Error(job.error || '任务失败');
        if (job.status === 'completed') return job;
        await new Promise((r) => setTimeout(r, 2000));
      }
    }

    async function process() {
      try {
        setStatusText('等待处理队列分配…');
        let job = run.job;
        if (job && job.status === 'queued') {
          job = await waitForProcessing(job.jobId);
          if (disposed) return;
          if (job.status === 'completed') {
            // Extremely unlikely for a brand-new job; treat as error.
            throw new Error('任务状态异常');
          }
        }
        setWaiting(false);

        const baseUrl = new URL(document.baseURI).origin;
        const backend = await detectBackend();
        setStatusText(`正在加载 OCR 引擎（${backend}）…`);
        const ocr = await createBrowserOcrEngine(baseUrl);
        await ocr.init();
        if (disposed) {
          await ocr.terminate();
          return;
        }

        setStatusText('正在渲染并识别页面…');
        const renderer = await createPdfRenderer(run.sourceBytes, 3);
        const sourcePageCount = renderer.pageCount;
        setTotal(sourcePageCount);

        const pipeline = await runSplitPipeline({
          sourcePdfBytes: new Uint8Array(run.sourceBytes),
          sourceFileName: run.sourceFile.name,
          pageCount: sourcePageCount,
          renderPage: renderer.renderPage,
          ocr,
          isCancelled: () => cancelledRef.current,
          callbacks: {
            onStage: (s, detail) => {
              if (disposed) return;
              setStage(s);
              setStageDetail(detail ?? '');
              if (s === 'parse') setStatusText('正在解析 PDF…');
              else if (s === 'recognize') setStatusText(detail ?? '正在识别页面…');
              else if (s === 'group') setStatusText('正在按 Tracking Number 分组…');
              else if (s === 'generate') setStatusText(detail ?? '正在生成 PDF…');
              else if (s === 'done') setStatusText('正在保存结果…');
            },
            onProgress: (cur, tot) => {
              if (disposed) return;
              setCurrent(cur);
              setTotal(tot);
            },
          },
        });

        if (disposed) {
          await ocr.terminate();
          renderer.destroy();
          return;
        }

        if (isLocal) {
          await ocr.terminate();
          renderer.destroy();
          if (!disposed) onFinished(null, pipeline);
          return;
        }

        // Upload generated outputs to the server (best effort).
        setStatusText('正在上传输出文件…');
        try {
          const files = [...pipeline.files.keys()];
          const tickets = await api.getOutputUploadTickets(job!.jobId, files);
          for (const ticket of tickets) {
            const bytes = pipeline.files.get(ticket.fileName);
            if (bytes) await api.uploadOutput(ticket.url, bytes);
          }
        } catch {
          // Non-fatal: outputs are still available in the browser.
        }

        setStatusText('正在完成…');
        const completed = await api.completeJob(job!.jobId, {
          sourcePageCount,
          outputs: pipeline.outputs,
          unresolvedPages: pipeline.unresolvedPages,
          pageResults: pipeline.pageResults,
          summary: pipeline.summary,
          report: pipeline.jsonReport,
        });
        await ocr.terminate();
        renderer.destroy();
        if (!disposed) onFinished(completed, pipeline);
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        console.error('[pipeline]', e);
        if (!disposed) {
          if (run.job) {
            try {
              await api.failJob(run.job.jobId, message);
            } catch {
              /* ignore */
            }
          }
          onError(message);
        }
      }
    }

    void process();
    return () => {
      disposed = true;
      cancelledRef.current = true;
    };
  }, [run, onFinished, onError]);

  const stages = STAGES.map((s) =>
    s.key === 'upload' && isLocal ? { ...s, label: '读取文件' } : s,
  );
  const stageIndex = stages.findIndex((s) => s.key === stage);
  const overallProgress =
    stage === 'done' ? 1 : total > 0 ? Math.max(0.05, current / total) : 0.05;

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m} 分 ${sec} 秒`;
  };

  return (
    <div className="page processing-page">
      <h1>正在处理：{run.sourceFile.name}</h1>

      {waiting ? (
        <div className="alert info">
          当前已有 5 个任务在处理，本任务已进入队列。请保持页面打开，系统会自动开始处理。
        </div>
      ) : (
        <div className="progress-wrap big">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${overallProgress * 100}%` }} />
          </div>
          <div className="progress-caption">
            {total > 0 && stage !== 'done' && (
              <span>
                正在处理第 {current} / {total} 页
              </span>
            )}
            <span className="elapsed">已用时 {fmt(elapsed)}</span>
          </div>
        </div>
      )}

      <ol className="stage-list">
        {stages.map((s, i) => (
          <li key={s.key} className={i <= stageIndex ? 'active' : ''}>
            <span className="stage-index">{i + 1}</span>
            {s.label}
          </li>
        ))}
      </ol>

      <div className="status-text">{statusText}</div>
      {stageDetail && <div className="status-detail">{stageDetail}</div>}

      <div className="alert warn">
        处理在浏览器本地执行（OCR + PDF 拆分），请保持此页面打开；关闭页面会中断当前处理。
      </div>

      <button className="ghost" onClick={onCancel} disabled={stage === 'done'}>
        取消并返回
      </button>
    </div>
  );
}
